import { Injectable, NgZone, inject } from '@angular/core';
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { take } from 'rxjs/operators';
import type { PwaInstallMode, PwaInstallSheetData } from '../shared/components/bottom-sheet/pwa-install/pwa-install.component';

const PROMPT_DELAY_MS = 3 * 60 * 1000;
const OVERLAY_RETRY_MS = 30_000;
const INSTALLED_KEY = 'kcms.pwa.installed';

interface BeforeInstallPromptEvent extends Event {
   prompt(): Promise<void>;
   userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

/** Convite de instalação no celular, 3 min após abrir. Some depois de instalado. */
@Injectable({ providedIn: 'root' })
export class PwaInstallService {
   private readonly zone = inject(NgZone);
   private readonly bottomSheet = inject(MatBottomSheet);
   private readonly abort = new AbortController();
   private started = false;
   private promptDue = false;
   private sheetShown = false;
   private opening = false;
   private deferredPrompt: BeforeInstallPromptEvent | null = null;
   private sheetRef: MatBottomSheetRef | null = null;
   private timer?: ReturnType<typeof setTimeout>;

   start(): void {
      if (this.started || typeof window === 'undefined') return;
      this.started = true;
      if (!window.isSecureContext || this.isStandalone() || !this.isHandheld()) return;
      if (this.isIos() && this.readInstalled()) return;

      this.zone.runOutsideAngular(() => {
         window.addEventListener('beforeinstallprompt', this.onBeforeInstallPrompt, { signal: this.abort.signal });
         window.addEventListener('appinstalled', this.onAppInstalled, { signal: this.abort.signal });
         this.scheduleWorker();
         this.arm(PROMPT_DELAY_MS);
      });
   }

   async install(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
      const promptEvent = this.deferredPrompt;
      this.deferredPrompt = null;
      if (!promptEvent) return 'unavailable';

      try {
         await promptEvent.prompt();
         const choice = await promptEvent.userChoice;
         if (choice.outcome === 'accepted') this.confirmInstalled();
         return choice.outcome;
      } catch {
         return 'unavailable';
      }
   }

   confirmInstalled(): void {
      this.writeInstalled();
      this.deferredPrompt = null;
      this.stop();
   }

   private readonly onBeforeInstallPrompt = (event: Event): void => {
      event.preventDefault();
      this.deferredPrompt = event as BeforeInstallPromptEvent;
      if (this.promptDue) this.queueOpen();
   };

   private readonly onAppInstalled = (): void => {
      this.confirmInstalled();
      this.zone.run(() => this.sheetRef?.dismiss());
   };

   private queueOpen(): void {
      if (this.timer !== undefined) {
         clearTimeout(this.timer);
         this.timer = undefined;
      }
      if (this.sheetShown || this.isStandalone()) return;
      this.promptDue = true;

      if (this.deferredPrompt) {
         this.openSheet('native');
         return;
      }

      if (this.isIos() && !this.readInstalled()) this.openSheet('ios');
   }

   private openSheet(mode: PwaInstallMode): void {
      if (this.sheetShown || this.opening) return;
      if (this.isOverlayOpen()) {
         this.arm(OVERLAY_RETRY_MS);
         return;
      }

      this.sheetShown = true;
      this.opening = true;
      void this.loadAndOpen(mode);
   }

   private async loadAndOpen(mode: PwaInstallMode): Promise<void> {
      try {
         const { PwaInstallComponent } = await import('../shared/components/bottom-sheet/pwa-install/pwa-install.component');
         this.zone.run(() => {
            if (this.sheetRef || this.isStandalone() || (this.readInstalled() && !this.deferredPrompt) || this.isOverlayOpen()) {
               this.sheetShown = false;
               if (!this.isStandalone() && !(this.readInstalled() && !this.deferredPrompt)) this.arm(OVERLAY_RETRY_MS);
               return;
            }

            const data: PwaInstallSheetData = { mode };
            this.sheetRef = this.bottomSheet.open(PwaInstallComponent, { data });
            this.sheetRef
               .afterDismissed()
               .pipe(take(1))
               .subscribe(() => {
                  this.sheetRef = null;
               });
         });
      } catch {
         this.sheetShown = false;
      } finally {
         this.opening = false;
      }
   }

   private scheduleWorker(): void {
      const register = () => this.registerWorker();
      const requestIdle = window.requestIdleCallback;
      if (typeof requestIdle === 'function') {
         requestIdle(register, { timeout: 2000 });
         return;
      }
      setTimeout(register, 1);
   }

   private registerWorker(): void {
      if (!('serviceWorker' in navigator)) return;

      const workerUrl = new URL('sw.js', document.baseURI);
      if (workerUrl.origin !== window.location.origin) return;

      const scope = new URL('./', document.baseURI);
      navigator.serviceWorker.register(workerUrl, { scope: scope.href, updateViaCache: 'none' }).catch(() => undefined);
   }

   private arm(delay: number): void {
      this.zone.runOutsideAngular(() => {
         if (this.timer !== undefined) clearTimeout(this.timer);
         this.timer = window.setTimeout(() => this.queueOpen(), delay);
      });
   }

   private stop(): void {
      if (this.timer !== undefined) {
         clearTimeout(this.timer);
         this.timer = undefined;
      }
      this.abort.abort();
   }

   private isOverlayOpen(): boolean {
      return !!document.querySelector('.mat-bottom-sheet-container, .mat-mdc-dialog-container');
   }

   private isHandheld(): boolean {
      const uaData = (navigator as Navigator & { userAgentData?: { mobile?: boolean } }).userAgentData;
      if (uaData?.mobile === true) return true;
      return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
   }

   private isIos(): boolean {
      if (/iP(hone|ad|od)/.test(navigator.userAgent)) return true;
      return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
   }

   private isStandalone(): boolean {
      if (window.matchMedia('(display-mode: standalone)').matches) return true;
      return (navigator as Navigator & { standalone?: boolean }).standalone === true;
   }

   private readInstalled(): boolean {
      try {
         return localStorage.getItem(INSTALLED_KEY) === '1';
      } catch {
         return false;
      }
   }

   private writeInstalled(): void {
      try {
         localStorage.setItem(INSTALLED_KEY, '1');
      } catch {
         /* modo privado */
      }
   }
}
